import {
  createPathParamCRUD,
  getPathParam,
  createPathParam,
  type NoPathParams,
} from "@/api/shared/crud";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type {
  TTreatmentPlan,
  TTreatmentPlanQueryParams,
  TTreatmentPlanImportResponse,
  TTreatmentDefaultOptions,
  ECooperateStaffType,
  TAddCooperateStaffPayload,
  TActionableCounts,
} from "./types";
import type { ECooperatePartiesType } from "@/api/models/treatmentPlan/job";
import type { TPracticeStaff } from "@/api/models/practice/practiceStaff";
import { createService } from "@/api/shared/service";

export * from "./types";

/**
 * The cooperate staff linked to a plan for a given role (`tc`, `gp`, `ortho`),
 * or `undefined` if no such link exists. Mirrors `getStaffByType` for patient links.
 */
export const getCooperateStaff = (
  plan: Pick<TTreatmentPlan, "cooperate_staff_links"> | null | undefined,
  type: ECooperateStaffType
) => plan?.cooperate_staff_links?.find((link) => link.link_type === type)?.staff;

/**
 * The cooperate practice linked to a plan for a given role, or `undefined` if none.
 */
export const getCooperatePractice = (
  plan: Pick<TTreatmentPlan, "cooperate_practice_links"> | null | undefined,
  type: ECooperatePartiesType
) => plan?.cooperate_practice_links?.find((link) => link.link_type === type)?.practice;

const SLUG = "treatment_plan";

export default createService(SLUG, {
  ...createPathParamCRUD<
    TTreatmentPlan,
    NoPathParams,
    TTreatmentPlanQueryParams & DefaultPaginatedFilters,
    Partial<TTreatmentPlan>,
    Partial<TTreatmentPlan>
  >(SLUG),

  getOfPatient: getPathParam<TTreatmentPlan, { id: string }>(`${SLUG}/patient/{id}`),

  defaultForPatient: getPathParam<TTreatmentPlan, TTreatmentDefaultOptions>(
    `${SLUG}/default-for-patient/{patientId}/{treatmentType}`
  ),

  duplicate: createPathParam<TTreatmentPlan, { id: string }, { name: string }>(
    `${SLUG}/{id}/clone`
  ),

  import: createPathParam<
    TTreatmentPlanImportResponse,
    { id: string },
    { data: Partial<TTreatmentPlan>; apply_changes: boolean }
  >(`${SLUG}/{id}/import`),

  export: getPathParam<{ data: TTreatmentPlan }, { id: string }>(`${SLUG}/{id}/export`),

  listOfAvailableCooperatePractices: getPathParam<
    string[],
    { treamentPlanId: string; practiceType: ECooperatePartiesType }
  >(`${SLUG}/patient/{treamentPlanId}/list-available-cooperate-practices/{practiceType}/`),

  listOfAvailableCooperateStaff: getPathParam<
    TPracticeStaff[],
    { treamentPlanId: string; staffType: ECooperateStaffType }
  >(`${SLUG}/patient/{treamentPlanId}/list-available-cooperate-staff/{staffType}/`),

  actionableCounts: getPathParam<TActionableCounts, Record<never, never>>(
    `${SLUG}/actionable-counts/`
  ),

  addCooperateStaff: createPathParam<
    void,
    { treamentPlanId: string; staffType: ECooperateStaffType },
    TAddCooperateStaffPayload
  >(`${SLUG}/patient/{treamentPlanId}/add-cooperate-staff/{staffType}/`),

  archive: createPathParam<void, { treatmentPlanId: string }, Record<never, never>>(
    `${SLUG}/patient/{treatmentPlanId}/archive/`
  ),
});
